import { Injector } from '@angular/core';
import { Store } from '@ngrx/store';

import { CreateTransactionMutation } from 'src/generated/graphql.generated';

import { JobFinancialsActions } from '../../job/job-financials/job-financials.actions';

import {
    BLUESAGE_MOCK_RAIL_LS_KEY,
    BLUESAGE_MOCK_SCENARIOS,
    activeMockScenarioName,
    isMockRailAvailable,
} from './bluesage-mock-rail.config';
import { buildInjectedHold } from './bluesage-mock-rail.link';


function jobIdFromUrl(): string | undefined {
    const [ , id ] = /\/jobs\/([^/?#]+)/.exec(window.location.pathname) ?? [];
    return id;
}

const DEFAULT_HOLD_MINOR = 25_000;

interface BlueSageMockConsoleApi {
    list(): void;
    use(scenario: string): void;
    off(): void;
    current(): string | null;
    injectHold(amountMinor?: number, jobId?: string): void;
}

export function installBlueSageMockRailConsole(injector: Injector): void {
    if (!isMockRailAvailable()) { return; }

    const api: BlueSageMockConsoleApi = {

        list() {
            const active = activeMockScenarioName();
            // eslint-disable-next-line no-console
            console.log(
                `BlueSage mock rail — ${ active ? `ON (${ active })` : 'OFF' }\n`
                + 'blueSageMock.use("<name>") to switch, blueSageMock.off() to stop.\n',
            );
            // eslint-disable-next-line no-console
            console.table(
                Object.entries(BLUESAGE_MOCK_SCENARIOS).map(([ name, scenario ]) => ({
                    scenario: name === active ? `▶ ${ name }` : name,
                    does: scenario.label,
                })),
            );
        },

        use(scenario: string) {
            if (!BLUESAGE_MOCK_SCENARIOS[scenario]) {
                // eslint-disable-next-line no-console
                console.error(
                    `No such scenario "${ scenario }". Run blueSageMock.list() for the set.`,
                );
                return;
            }

            localStorage.setItem(BLUESAGE_MOCK_RAIL_LS_KEY, scenario);
            // eslint-disable-next-line no-console
            console.log(
                `BlueSage mock rail → ${ scenario }. `
                + 'Reopen the dialog; no reload needed.',
            );
        },

        off() {
            localStorage.removeItem(BLUESAGE_MOCK_RAIL_LS_KEY);
            // eslint-disable-next-line no-console
            console.log('BlueSage mock rail OFF — every call goes to the real backend again.');
        },

        current() {
            return activeMockScenarioName();
        },

        injectHold(amountMinor = DEFAULT_HOLD_MINOR, jobId = jobIdFromUrl()) {
            if (!jobId) {
                // eslint-disable-next-line no-console
                console.error(
                    'No job id — open a job page first, or pass one: '
                    + 'blueSageMock.injectHold(25000, "<jobId>")',
                );
                return;
            }

            const data = buildInjectedHold({ jobId, amountMinor });

            injector.get(Store).dispatch(JobFinancialsActions.createTransactionSuccess({
                // A mocked payload cannot reproduce the `Job` scalars the generated type selects
                // and nothing on this path reads.
                data: data as unknown as CreateTransactionMutation,
            }));

            // eslint-disable-next-line no-console
            console.log(
                `Injected a $${ (amountMinor / 100).toFixed(2) } authorization hold on ${ jobId }.`,
            );
        },
    };

    (window as any).blueSageMock = api;

    if (activeMockScenarioName()) {
        // eslint-disable-next-line no-console
        console.warn(
            `⚠ BlueSage MOCK RAIL is ON (${ activeMockScenarioName() }). `
            + 'No BlueSage call leaves this browser and no transaction is saved. '
            + 'blueSageMock.list() for scenarios, blueSageMock.off() to stop.',
        );
    }
}
